import instantsearch from 'instantsearch.js';
import { configure, searchBox, clearRefinements, hits } from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
import searchClient from './search-client';
import { customPagination } from './pagination';
import { HitHelpers } from './hit-helpers';
import { refinementListWithPanel } from './panel';
import { refinementListTemplate } from './templates/refinement-list';

class ArticleSearch {
  constructor(rootEl) {
    this.rootEl = rootEl;
    if (!this.rootEl) {
      throw new Error('Root article search element not found.');
    }

    this.siteId = Number(this.rootEl.dataset['siteId']);

    this.hitsPerPage = 24;

    this.search = instantsearch({
      indexName: this.rootEl.dataset['algoliaIndex'],
      searchClient: searchClient,
      routing: {
        router: history({
          cleanUrlOnDispose: true,
        }),
      },
      insights: true,
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    this.init();
    this.search.start();
  }

  init() {
    // Function to extract the numeric value after "publish_date: "
    function extractPublishDate(label) {
      const match = label.match(/publish_date:\s*(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    }
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter results by 'type' facet
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: `type:"post"${this.siteId !== 1 ? ` AND blogID:${this.siteId}` : ''} OR type:"prattfolio-story"`,
      }),

      searchBox({
        container: '.js-article-search-input',
        placeholder: 'Search Articles',
        showSubmit: false,
      }),

      clearRefinements({
        container: '.js-article-search-clear',
        templates: {
          resetLabel: 'Reset',
        },
      }),

      refinementListWithPanel('Type')({
        container: '.js-article-search-types-input',
        attribute: 'type',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
        transformItems: items =>
          items.map(item => ({
            ...item,
            highlighted: HitHelpers.getTypeDisplay(item.highlighted),
          })),
      }),

      refinementListWithPanel('Prattfolio Department')({
        container: '.js-article-search-prattfolio-departments-input',
        attribute: 'prattfolio_departments',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
        showMore: true,
      }),

      refinementListWithPanel('Prattfolio Issue')({
        container: '.js-article-search-issues-input',
        attribute: 'issue',
        operator: 'and',
        templates: {
          item: refinementListTemplate,
        },
        transformItems: items => {
          // Sort items by the numeric value after "publish_date: "
          items.sort((a, b) => {
            const dateA = extractPublishDate(a.label);
            const dateB = extractPublishDate(b.label);

            if (dateA && dateB) {
              return dateB - dateA;
            }

            return 0;
          });

          // Modify items to display only the string before "|"
          return items.map(item => {
            const label = item.label.split(' | ')[0]; // Extract the string before "|"
            return {
              ...item,
              highlighted: label,
            };
          });
        },
      }),

      refinementListWithPanel('School')({
        container: '.js-article-search-schools-input',
        attribute: 'schools',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Department')({
        container: '.js-article-search-departments-input',
        attribute: 'departments',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Office')({
        container: '.js-article-search-offices-input',
        attribute: 'offices',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: '.js-article-search-hits',
        templates: {
          /* eslint-disable */
          item(hit) {
            // prettier-ignore
            return `
              <div class="article-hit">
                <div class="article-hit-inner">
                  <div class="article-hit__titles">
                    ${window.PRATT_SITES
                      && hit.blogID !== 1
                      && window.PRATT_SITES[hit.blogID] ? `
                      <p class="article-hit__callout">${window.PRATT_SITES[hit.blogID]}</p>
                    ` : ''}
                    <h3 class="article-hit__title">
                      <a href="${hit.url}" class="article-hit__title-link" onClick="sendEvent('click','${hit.type} clicked', '${hit.objectID}', '${hit.__queryID}', ${hit.__position})">
                        <span class="article-hit__title-text">${hit.title}</span>
                      </a>
                    </h3>
                  </div>

                  ${hit.excerpt ? `
                    <div class="article-hit__content">
                      ${hit.excerpt.replace('[&hellip;]', '&hellip;')}
                    </div>
                  ` : ''}
                  ${hit.thumbnail ? `
                    <div class="article-hit__image">
                      <img src="${hit.thumbnail}" alt="${hit.title}" />
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
          },
          /* eslint-enable */
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      customPagination({
        container: document.querySelector('.js-article-search-pagination'),
      }),
    ]);
  }
}

export default ArticleSearch;
